import api from "../api";
import type {
  AccessCatalog,
  AccessGroup,
  AccessGroupChoice,
  AccessGroupPayload,
  AccessGroupRef,
  ClientScopeKey,
  DemoClientsKey,
  ReachPreview,
  ServiceTeamsAccess,
} from "types/interfaces/access";

const BASE = "/api/access-groups";
const userGroupTag = (userId: number) =>
  ({ type: "AccessGroups", id: `user-${userId}` }) as const;

const accessGroupsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAccessCatalog: builder.query<AccessCatalog, void>({
      query: () => `${BASE}/catalog/`,
    }),
    getServiceTeams: builder.query<ServiceTeamsAccess, void>({
      query: () => `${BASE}/service-teams/`,
    }),
    getReachPreview: builder.query<
      ReachPreview,
      {
        client_scope: ClientScopeKey;
        team: string | null;
        demo_clients: DemoClientsKey;
      }
    >({
      query: ({ client_scope, team, demo_clients }) => ({
        url: `${BASE}/reach-preview/`,
        params: { client_scope, demo_clients, ...(team ? { team } : {}) },
      }),
    }),
    getAccessGroups: builder.query<AccessGroup[], void>({
      query: () => `${BASE}/`,
      providesTags: ["AccessGroups"],
    }),
    createAccessGroup: builder.mutation<AccessGroup, AccessGroupPayload>({
      query: (body) => ({ url: `${BASE}/`, method: "POST", body }),
      invalidatesTags: ["AccessGroups"],
    }),
    updateAccessGroup: builder.mutation<
      AccessGroup,
      { id: number } & AccessGroupPayload
    >({
      query: ({ id, ...body }) => ({
        url: `${BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      // Members' menus follow their group's tiers; the users table shows the
      // group's name.
      invalidatesTags: ["AccessGroups", "Auth", "AdminUsers"],
    }),
    deleteAccessGroup: builder.mutation<void, number>({
      query: (id) => ({ url: `${BASE}/${id}/`, method: "DELETE" }),
      invalidatesTags: ["AccessGroups", "AdminUsers"],
    }),
    getUserAccessGroup: builder.query<
      { group: AccessGroupRef | null; choices?: AccessGroupChoice[] },
      number
    >({
      query: (userId) => `/api/admin/platform-users/${userId}/access-group/`,
      providesTags: (_result, _error, userId) => [userGroupTag(userId)],
    }),
    setUserAccessGroup: builder.mutation<
      { group: AccessGroupRef | null },
      { userId: number; group: number | null }
    >({
      query: ({ userId, group }) => ({
        url: `/api/admin/platform-users/${userId}/access-group/`,
        method: "PUT",
        body: { group },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        userGroupTag(userId),
        "AccessGroups",
        "AdminUsers",
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAccessCatalogQuery,
  useGetServiceTeamsQuery,
  useGetReachPreviewQuery,
  useGetAccessGroupsQuery,
  useCreateAccessGroupMutation,
  useUpdateAccessGroupMutation,
  useDeleteAccessGroupMutation,
  useGetUserAccessGroupQuery,
  useSetUserAccessGroupMutation,
} = accessGroupsApi;

export default accessGroupsApi;
